/**
 * LmsProvider: the Learning Center's module context (plan 4.8). Mounted once
 * in App.tsx's ModuleProviders so it persists across cross-module navigation,
 * the same as every other module provider.
 *
 * It exposes the current site (every /api/lms request is site-scoped and
 * carries X-Site-Id, plan 4.9) and the caller's identity from GET /api/lms/me
 * (plan 4.2: the frontend gates the two admin tabs on isLmsAdmin).
 *
 * The identity is loaded LAZILY: the provider wraps the whole app, so it never
 * fetches on its own (a non-entitled user on any other page must not fire a
 * guarded call). The first useLmsMe() consumer, which only exists inside the
 * module shell after LmsApp's entitlement check passed, asks for it through
 * loadMe(). The answer is kept per site: a site switch drops it (the admin flag
 * is per site, plan 4.2) and the next consumer loads it again. loadMe() is
 * idempotent for a site (loading, ready and failed all no-op); refreshMe()
 * forces a new request (the Retry control). A response for a site that is no
 * longer current is discarded.
 *
 * The summary (GET /api/lms/me/summary, 9.4) is polled every SUMMARY_POLL_MS
 * (60 s, D14 and 4.7: polling, no SSE) and only while all of these hold: at
 * least one useLmsSummary() consumer is mounted (the module bell, WP-48; so
 * again nothing fires outside the module shell), the identity for the current
 * site has loaded (the same guard answers both, so a failed or refused /me
 * never starts a poll), and the page is visible (document.visibilityState; a
 * hidden tab asks nothing and asks once at once when it is shown again). One
 * request is in flight at a time (a second ask while one runs is coalesced
 * into one follow-up); a failed poll keeps the last answer and says nothing,
 * because it is a background read and the next tick tries again. An answer
 * for a site that is no longer current is discarded. The answer lives in its
 * own context (read through useLmsSummary), so a poll re-renders the bell and
 * nothing else: the module context below changes only with the site and the
 * identity.
 *
 * App.tsx mounts this provider OUTSIDE the Router (ModuleProviders wraps
 * <Router>), so nothing here may use a router hook (useLocation, useNavigate,
 * useParams); the test harness mounts it outside its MemoryRouter for the
 * same reason (plan 18.5).
 *
 * Hot file (plan 15.12): WP-03, then WP-48 (the summary polling).
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { apiErrorMessage, getMe } from './api/client';
import type { LmsMe } from './api/client';
import { getMySummary } from './api/me';
import type { MySummary } from './api/me';

/** D14, 4.7, 9.4: the summary is asked for every 60 seconds while the page is visible. */
export const SUMMARY_POLL_MS = 60_000;

export type LmsMeStatus = 'idle' | 'loading' | 'ready' | 'error';

export interface LmsContextValue {
  /** Active site id, or null when none is selected. Every /api/lms request needs it as X-Site-Id. */
  siteId: string | null;
  siteName: string | null;
  networkId: string | null;
  isSuperAdmin: boolean;
  /** GET /api/lms/me for the current site; null until loaded. */
  me: LmsMe | null;
  meStatus: LmsMeStatus;
  meError: string | null;
  /** Load me for the current site once (no-op while loading, after success and after a failure). */
  loadMe: () => void;
  /** Load me again for the current site, whatever the state. */
  refreshMe: () => void;
  /** Register a summary consumer; the poll runs while at least one is registered. Returns the release. */
  subscribeSummary: () => () => void;
  /** Ask for the summary again now (the bell opening), while the poll is running. */
  refreshSummary: () => void;
  /** Set the unread count the summary carries (the bell's inbox after a read) until the next answer. */
  noteUnreadNotifications: (unread: number) => void;
}

interface MeState {
  siteId: string | null;
  status: LmsMeStatus;
  me: LmsMe | null;
  error: string | null;
}

interface SummaryState {
  siteId: string | null;
  summary: MySummary | null;
}

const IDLE: MeState = { siteId: null, status: 'idle', me: null, error: null };
const NO_SUMMARY: SummaryState = { siteId: null, summary: null };

/** True unless the browser says the page is hidden (a background tab, a minimised window). */
function pageVisible(): boolean {
  return typeof document === 'undefined' || document.visibilityState !== 'hidden';
}

const LmsContext = createContext<LmsContextValue | undefined>(undefined);
/** GET /api/lms/me/summary for the current site (9.4); null until the first answer. Kept apart so a poll re-renders only its readers. */
const LmsSummaryContext = createContext<MySummary | null>(null);

export function LmsProvider({ children }: { children: ReactNode }) {
  const { currentSite, isSuperAdmin } = useAuth();
  const siteId = currentSite?.id ?? null;

  const [meState, setMeState] = useState<MeState>(IDLE);
  // Mirrors for the stable callbacks: the latest state and the current site.
  const meStateRef = useRef<MeState>(IDLE);
  const siteIdRef = useRef<string | null>(siteId);
  siteIdRef.current = siteId;

  const commit = useCallback((next: MeState) => {
    meStateRef.current = next;
    setMeState(next);
  }, []);

  // A site switch drops the previous site's identity at once, so nothing reads
  // an admin flag that belonged to another site.
  useEffect(() => {
    if (meStateRef.current.siteId !== siteId) commit({ ...IDLE, siteId });
  }, [siteId, commit]);

  const fetchMe = useCallback(
    (forSite: string) => {
      commit({ siteId: forSite, status: 'loading', me: null, error: null });
      getMe()
        .then((me) => {
          if (siteIdRef.current !== forSite) return;
          commit({ siteId: forSite, status: 'ready', me, error: null });
        })
        .catch((err) => {
          if (siteIdRef.current !== forSite) return;
          commit({
            siteId: forSite,
            status: 'error',
            me: null,
            error: apiErrorMessage(err, 'Could not load your Learning Center profile.'),
          });
        });
    },
    [commit]
  );

  const loadMe = useCallback(() => {
    const forSite = siteIdRef.current;
    if (!forSite) return;
    const cur = meStateRef.current;
    if (cur.siteId === forSite && cur.status !== 'idle') return;
    fetchMe(forSite);
  }, [fetchMe]);

  const refreshMe = useCallback(() => {
    const forSite = siteIdRef.current;
    if (!forSite) return;
    fetchMe(forSite);
  }, [fetchMe]);

  // Only the answer for the current site is ever exposed.
  const current = meState.siteId === siteId ? meState : IDLE;

  // -------------------------------------------------------------------------
  // The summary poll (9.4, D14)
  // -------------------------------------------------------------------------

  const [summaryState, setSummaryState] = useState<SummaryState>(NO_SUMMARY);
  const [summaryConsumers, setSummaryConsumers] = useState(0);
  const [visible, setVisible] = useState<boolean>(pageVisible);
  // The site the poll is running for (null while it is not), for the stable callbacks.
  const pollSiteRef = useRef<string | null>(null);
  const summaryInFlight = useRef(false);
  const summaryAgain = useRef(false);

  const fetchSummary = useCallback(() => {
    const run = () => {
      const forSite = pollSiteRef.current;
      if (!forSite) return;
      if (summaryInFlight.current) {
        summaryAgain.current = true;
        return;
      }
      summaryInFlight.current = true;
      summaryAgain.current = false;
      getMySummary()
        .then((summary) => {
          if (siteIdRef.current !== forSite || !summary || typeof summary !== 'object') return;
          setSummaryState({ siteId: forSite, summary });
        })
        .catch(() => {
          // A background read: the last answer stays and the next tick asks again (9.4).
        })
        .finally(() => {
          summaryInFlight.current = false;
          if (summaryAgain.current) {
            summaryAgain.current = false;
            run();
          }
        });
    };
    run();
  }, []);

  const hasSummaryConsumers = summaryConsumers > 0;
  const meReady = meState.siteId === siteId && meState.status === 'ready';
  const pollSite = hasSummaryConsumers && visible && meReady ? siteId : null;

  // Visibility gating: listened to only while someone wants the summary.
  useEffect(() => {
    if (!hasSummaryConsumers || typeof document === 'undefined') return undefined;
    const onChange = () => setVisible(pageVisible());
    onChange();
    document.addEventListener('visibilitychange', onChange);
    return () => document.removeEventListener('visibilitychange', onChange);
  }, [hasSummaryConsumers]);

  // Ask at once when the poll starts (a consumer mounted, the identity loaded,
  // the page shown again, a site switch), then every SUMMARY_POLL_MS.
  useEffect(() => {
    pollSiteRef.current = pollSite;
    if (!pollSite) return undefined;
    fetchSummary();
    const timer = window.setInterval(fetchSummary, SUMMARY_POLL_MS);
    return () => {
      window.clearInterval(timer);
      if (pollSiteRef.current === pollSite) pollSiteRef.current = null;
    };
  }, [pollSite, fetchSummary]);

  const subscribeSummary = useCallback(() => {
    setSummaryConsumers((n) => n + 1);
    let released = false;
    return () => {
      if (released) return;
      released = true;
      setSummaryConsumers((n) => Math.max(0, n - 1));
    };
  }, []);

  const refreshSummary = useCallback(() => {
    fetchSummary();
  }, [fetchSummary]);

  const noteUnreadNotifications = useCallback((unread: number) => {
    if (!Number.isFinite(unread)) return;
    setSummaryState((prev) =>
      prev.summary && prev.siteId === siteIdRef.current
        ? { siteId: prev.siteId, summary: { ...prev.summary, unread_notifications: Math.max(0, Math.floor(unread)) } }
        : prev
    );
  }, []);

  const summary = summaryState.siteId === siteId ? summaryState.summary : null;

  const value = useMemo<LmsContextValue>(
    () => ({
      siteId,
      siteName: currentSite?.name ?? null,
      networkId: currentSite?.network_id ?? null,
      isSuperAdmin,
      me: current.me,
      meStatus: current.status,
      meError: current.error,
      loadMe,
      refreshMe,
      subscribeSummary,
      refreshSummary,
      noteUnreadNotifications,
    }),
    [
      siteId,
      currentSite?.name,
      currentSite?.network_id,
      isSuperAdmin,
      current.me,
      current.status,
      current.error,
      loadMe,
      refreshMe,
      subscribeSummary,
      refreshSummary,
      noteUnreadNotifications,
    ]
  );

  return (
    <LmsContext.Provider value={value}>
      <LmsSummaryContext.Provider value={summary}>{children}</LmsSummaryContext.Provider>
    </LmsContext.Provider>
  );
}

export function useLms(): LmsContextValue {
  const ctx = useContext(LmsContext);
  if (!ctx) throw new Error('useLms must be used within LmsProvider');
  return ctx;
}

export interface UseLmsSummaryResult {
  /** The current site's GET /me/summary answer (9.4), or null before the first one. */
  summary: MySummary | null;
  /** Ask again now (coalesced with a request in flight). */
  refresh: () => void;
  /** Set the unread count locally until the next answer (the bell's inbox after a read). */
  noteUnread: (unread: number) => void;
}

/**
 * useLmsSummary: subscribe to the summary poll while the calling component is
 * mounted (the module bell is the one consumer in the shell). Like
 * useLmsMe(), calling it loads the identity for the current site, because the
 * poll starts only once the identity has loaded.
 */
export function useLmsSummary(): UseLmsSummaryResult {
  const { siteId, loadMe, subscribeSummary, refreshSummary, noteUnreadNotifications } = useLms();
  const summary = useContext(LmsSummaryContext);

  useEffect(() => {
    if (siteId) loadMe();
  }, [siteId, loadMe]);

  useEffect(() => subscribeSummary(), [subscribeSummary]);

  return { summary, refresh: refreshSummary, noteUnread: noteUnreadNotifications };
}

export default LmsProvider;
